import React, { memo } from "react";
import { useObserver } from "mobx-react";
import cns from "classnames";
import { Divider, Grid, makeStyles } from "@material-ui/core";
import locale, { getLocaleMonths } from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import { Body, SecondaryButton, RegularButton } from "@components";
import FormControls from "@components/forms/common/FormControls";
import { Text, Select } from "@components/formx";
import { useConsultantEditorContext } from "../ConsultantEditorContext";
import { ApproveBox } from "../components/Common/ApproveBox";

export const EducationInfo = memo(() => {
  const editorStore = useConsultantEditorContext();
  const { consultantCVManager: cvManager } = editorStore;
  const classes = useStyles({});

  return useObserver(() => {
    return (
      <>
        {editorStore.educationItems.length === 0 && (
          <Grid item>
            <Body color="textSecondary" style={{ textAlign: "center" }}>
              ({locale.EmptyHere.toLocaleLowerCase()})
            </Body>
          </Grid>
        )}

        {editorStore.educationItems.map((item, i) => {
          const itemName = `education.items.${i}`;
          const hasChangedRecord = cvManager.educationNotApproved(item);

          return (
            <Grid
              key={i}
              className={cns(classes.item, {
                [classes.notApproved]: hasChangedRecord
              })}
              container
              spacing={1}
              data-test="EducationCard"
            >
              {hasChangedRecord && (
                <ApproveBox onCheck={() => cvManager.approveEducation(item)} />
              )}

              <Grid item xs={12}>
                <Text label={locale.CourseName} name={`${itemName}.courseName`} />
              </Grid>

              <Grid item xs={12}>
                <Text label={locale.CourseLocation} name={`${itemName}.courseLocation`} />
              </Grid>

              <Grid container item xs={12} sm={6} alignItems="flex-end" spacing={1}>
                <Grid item xs={4}>
                  <Text label={locale.FromYear} name={`${itemName}.fromYear`} />
                </Grid>
                <Grid item xs={8}>
                  <Select
                    disabled={!item.fromYear}
                    fullWidth
                    label={locale.Month}
                    name={`${itemName}.fromMonth`}
                    options={getLocaleMonths().toLabelValue(
                      (v) => v,
                      (v, ind) => ind.toString()
                    )}
                    sortOptions={false}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={6} alignItems="flex-end" spacing={1}>
                <Grid item xs={4}>
                  <Text label={locale.ToYear} name={`${itemName}.toYear`} />
                </Grid>
                <Grid item xs={8}>
                  <Select
                    disabled={!item.toYear}
                    fullWidth
                    label={locale.Month}
                    name={`${itemName}.toMonth`}
                    options={getLocaleMonths().toLabelValue(
                      (v) => v,
                      (v, ind) => ind.toString()
                    )}
                    sortOptions={false}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} alignItems="flex-end" spacing={1}>
                <Select
                  fullWidth
                  label={locale.EducationType}
                  name={`${itemName}.educationType`}
                  options={["Course", "Degree"].toLabelValue((v) => locale[v])}
                  sortOptions={false}
                />
              </Grid>

              <FormControls
                fitted
                right={
                  <RegularButton
                    onClick={() => {
                      editorStore.removeEducation(i);
                      cvManager.approveEducation(item);
                    }}
                  >
                    {locale.Delete}
                  </RegularButton>
                }
              />

              <Grid item xs={12}>
                <Divider style={{ flex: 1 }} />
              </Grid>
            </Grid>
          );
        })}

        <FormControls
          fitted
          right={
            <SecondaryButton
              onClick={() => editorStore.addEducation()}
              variant="text"
              data-test="AddEducation"
            >
              {locale.Add}
            </SecondaryButton>
          }
        />
      </>
    );
  });
});

const useStyles = makeStyles((theme: ThemeType) => ({
  item: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing()
    }
  },
  notApproved: {
    ...theme.palette.error
  }
}));
